import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  Button,
  Space,
  Row,
  Col,
  Modal,
  Form,
  Input,
  Select,
  notification,
  Upload,
  Flex,
  Card,
  Tooltip,
} from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getLeadRequestsService,
  downloadGatePassService,
  createLeadRequestService,
  approveRejectLeadRequestService,
} from "../../../services/admin-services";
import {
  getLeadRequestTypesService,
  getDeliveryCancellationReasonsService,
} from "../../../services/master-services";
import {
  REQUEST_TYPE_CATEGORIES,
  REQUEST_TYPE_IDS,
} from "../../../utills/constant";
import { fetchLeadDetailsServices } from "../../../app/auth/slice/authSlice";
import dayjs from "dayjs";
import AllocateVinModal from "./AllocateVinModal";
import Loader from "../../../components/common/Loader";

const { Text, Title } = Typography;
const { TextArea } = Input;

const Requests = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { id: leadId } = useParams();
  const leadViewDetails = useSelector((state) => state?.auth?.leadDetails);
  const roleType = useSelector((state) => state?.auth?.userData?.role);
  const loggedInRoleId = useSelector((state) => state?.auth?.userData?.role_id);
  const isSuperAdmin = roleType === "Super Admin";

  // Check if role is Procurement Manager or Exchange Evaluation Team - they can only view, no actions
  const isViewOnlyRole =
    roleType === "Procurement Manager" ||
    roleType === "Exchange Evaluation Team" || roleType === 'Finance Manager' ||
    roleType === 'Back Office Manager';

  // Show buttons only for Super Admin and Sales Manager (but not for view-only roles)
  const canCreateRequest =
    !isViewOnlyRole &&
    (roleType === "Super Admin" || roleType === "Sales Manager");

  // Check if lead status is Open - if Open, disable New Request buttons
  const leadStatusName = leadViewDetails?.lead_status?.name;
  const isLeadOpen = leadStatusName === "Open" || leadStatusName === "open";

  // Allocation Request state
  const [allocationData, setAllocationData] = useState([]);
  const [allocationLoading, setAllocationLoading] = useState(false);
  const [allocationPagination, setAllocationPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [allocationSearch, setAllocationSearch] = useState("");

  // Delivery Request state
  const [deliveryData, setDeliveryData] = useState([]);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryPagination, setDeliveryPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [deliverySearch, setDeliverySearch] = useState("");

  // Cancellation Request state
  const [cancellationData, setCancellationData] = useState([]);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [cancellationPagination, setCancellationPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [cancellationSearch, setCancellationSearch] = useState("");

  const [isNewRequestModalOpen, setIsNewRequestModalOpen] = useState(false);
  const [requestTypeForModal, setRequestTypeForModal] = useState("");

  const [cancellationAttachments, setCancellationAttachments] = useState([]);
  const [isAllocateVinModalOpen, setIsAllocateVinModalOpen] = useState(false);
  const [requestTypes, setRequestTypes] = useState([]);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [selectedCancellationRequestType, setSelectedCancellationRequestType] =
    useState(null);

  // Approve/Reject modal state
  const [isApproveRejectModalOpen, setIsApproveRejectModalOpen] =
    useState(false);
  const [approveRejectForm] = Form.useForm();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'
  const [approveRejectLoading, setApproveRejectLoading] = useState(false);
  const [remarksModalVisible, setRemarksModalVisible] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");
  const [remarksModalTitle, setRemarksModalTitle] = useState("Remarks");

  const validateCancellationFile = (file) => {
    const isValidType =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/gif" ||
      file.type === "application/pdf";
    if (!isValidType) {
      notification.error({
        description: "You can only upload JPG/PNG/GIF/PDF file",
      });
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      notification.error({
        description: "File must be smaller than 10MB",
      });
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleCancellationFileChange = (info) => {
    setCancellationAttachments([...info.fileList]);
  };

  // Common function to fetch and filter requests by type
  const fetchRequestsByType = async (
    requestType,
    page,
    perPage,
    searchTerm,
    setData,
    setLoading,
    setPagination
  ) => {
    try {
      setLoading(true);

      // Build API params with appropriate flag based on request type
      const apiParams = {
        page,
        per_page: perPage,
        search: searchTerm || undefined,
        lead_id: leadId,
      };

      // Add request type flag
      if (requestType === "Allocation Request") {
        apiParams.allocation_requests = 1;
      } else if (requestType === "Delivery Request") {
        apiParams.delivery_requests = 1;
      } else if (requestType === "Cancellation Request") {
        apiParams.cancellation_requests = 1;
      }

      const res = await getLeadRequestsService(apiParams);
      const list = res?.data?.data?.list_data || [];
      const total = res?.data?.data?.total_records ?? 0;

      const rows = Array.isArray(list)
        ? list.map((item) => ({
          key: item.id,
          id: item.id,
          request_id: item.request_id || "-",
          request_type: item.request_type_name || item.request_type || "-",
          request_type_id: item.request_type_id || null,
          requested_by: item.created_by || "-",
          requested_on: item.created_at
            ? dayjs(item.created_at).format("DD/MM/YYYY")
            : "-",
          remarks_on_request: item.request_creation_remarks || "-",
          approved_rejected_on: item.approved_rejected_date
            ? dayjs(item.approved_rejected_date).format("DD/MM/YYYY")
            : "-",
          approved_rejected_by: item.approved_reject_by || "-",
          approval_status: item.approval_status,
          approval_status_name: item.approval_status_name || "-",
          approve_reject_remarks: item.approve_reject_remarks || "-",
          approval_role_id: item.approval_role_id || null,
          approval_role_name: item.approval_role_name || null,
          // Additional fields for Allocation Request
          chassis_number: item.chassis_number || null,
          request_location: item.request_location || item.location || null,
          // Additional fields for Cancellation Request
          customer_cancellation_letter:
            item.cancellation_letter_file_name ||
            item.customer_cancellation_letter ||
            null,
          cancellation_letter_path:
            item.cancellation_letter_path ||
            item.cancellation_letter_file_path ||
            null,
          cancellation_letter_file_name:
            item.cancellation_letter_file_name || null,
        }))
        : [];

      setData(rows);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: perPage,
        total: total,
      }));
      if (leadId) {
        dispatch(fetchLeadDetailsServices({ id: leadId }));
      }
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch all requests
  const fetchAllRequests = async () => {
    await Promise.all([
      fetchRequestsByType(
        "Allocation Request",
        allocationPagination.current,
        allocationPagination.pageSize,
        allocationSearch,
        setAllocationData,
        setAllocationLoading,
        setAllocationPagination
      ),
      fetchRequestsByType(
        "Delivery Request",
        deliveryPagination.current,
        deliveryPagination.pageSize,
        deliverySearch,
        setDeliveryData,
        setDeliveryLoading,
        setDeliveryPagination
      ),
      fetchRequestsByType(
        "Cancellation Request",
        cancellationPagination.current,
        cancellationPagination.pageSize,
        cancellationSearch,
        setCancellationData,
        setCancellationLoading,
        setCancellationPagination
      ),
    ]);
  };

  useEffect(() => {
    if (leadId) {
      fetchAllRequests();
    }
    fetchRequestTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const fetchRequestTypes = async () => {
    try {
      const res = await getLeadRequestTypesService();
      setRequestTypes(res?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    }
  };

  const fetchCancellationReasons = async () => {
    try {
      const res = await getDeliveryCancellationReasonsService();
      setCancellationReasons(res?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    }
  };

  // Helper function to check if buttons should be shown
  const shouldShowActionButtons = (record) => {
    // Special case for Document Collection Request:
    // Explicitly block Procurement and Exchange roles, but allow others (even if they are technically view-only in other contexts)
    // providing the request is Pending.
    if (
      record.request_type_id === REQUEST_TYPE_IDS.DOCUMENT_COLLECTION_REQUEST
    ) {
      if (
        roleType === "Procurement Manager" ||
        roleType === "Exchange Evaluation Team"
      ) {
        return false;
      }
      if (record.approval_status === 0) {
        return true;
      }
    }

    // Hide buttons for view-only roles for all other request types
    if (isViewOnlyRole) {
      return false;
    }

    return (
      record.approval_status === 0 &&
      (isSuperAdmin || record.approval_role_id === loggedInRoleId || ((record.approval_role_id === '' || record.approval_role_id === null) && loggedInRoleId !== 12))
    );
  };

  // Helper function to render remarks with tooltip and "See more"
  const renderRemarks = (text, title = "Remarks") => {
    const displayText = text || "-";
    if (!displayText || displayText === "-") return "-";

    const maxLength = 50;
    const isLongText = displayText.length > maxLength;
    const truncatedText = isLongText ? `${displayText.substring(0, maxLength)}...` : displayText;

    return (
      <div style={{ wordBreak: "break-word" }}>
        <Tooltip title={displayText} placement="topLeft">
          <span>{truncatedText}</span>
        </Tooltip>
        {isLongText && (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, marginLeft: 8, height: "auto" }}
            onClick={() => {
              setSelectedRemarks(displayText);
              setRemarksModalTitle(title);
              setRemarksModalVisible(true);
            }}
          >
            See more
          </Button>
        )}
      </div>
    );
  };

  // Allocation Request columns
  const getAllocationColumns = (onApprove, onReject) => [
    {
      title: "Request ID",
      dataIndex: "request_id",
      key: "request_id",
    },
    {
      title: "Chassis Number",
      dataIndex: "chassis_number",
      key: "chassis_number",
      render: (text) => text || "-",
    },
    {
      title: "Request type",
      dataIndex: "request_type",
      key: "request_type",
      render: (text, record) => {
        return (
          <div>
            <div>{text || "-"}</div>
            {record.approval_role_name && (
              <div>
                <Text strong>{record.approval_role_name}</Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Requested By",
      dataIndex: "requested_by",
      key: "requested_by",
    },
    {
      title: "Request Location",
      dataIndex: "request_location",
      key: "request_location",
      render: (text) => text || "-",
    },
    {
      title: "Requested On",
      dataIndex: "requested_on",
      key: "requested_on",
    },
    {
      title: "Remarks on Request",
      dataIndex: "remarks_on_request",
      key: "remarks_on_request",
      width: 200,
      render: (text) => renderRemarks(text, "Remarks on Request"),
    },
    {
      title: "Approval Status",
      dataIndex: "approval_status_name",
      key: "approval_status_name",
    },
    {
      title: "Approved / Rejected On",
      dataIndex: "approved_rejected_on",
      key: "approved_rejected_on",
      render: (text) => text || "-",
    },
    {
      title: "Remarks on Approve / Reject",
      dataIndex: "approve_reject_remarks",
      key: "approve_reject_remarks",
      width: 200,
      render: (text) => renderRemarks(text, "Remarks on Approve / Reject"),
    },
    {
      title: "Approved / Rejected By",
      dataIndex: "approved_rejected_by",
      key: "approved_rejected_by",
      render: (text) => text || "-",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const showButtons = shouldShowActionButtons(record);
        if (!showButtons) {
          return "-";
        }
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              className="secondary-button"
              onClick={() => onApprove(record, "Allocation Request")}
            >
              Approve
            </Button>
            <Button
              ghost
              danger
              size="small"
              className="primary-button"
              onClick={() => onReject(record, "Allocation Request")}
            >
              Reject
            </Button>
          </Space>
        );
      },
    },
  ];

  // Delivery Request columns
  const getDeliveryColumns = (onApprove, onReject) => [
    {
      title: "Request ID",
      dataIndex: "request_id",
      key: "request_id",
      render: (text) => (text && text !== "" ? text : "-"),
    },
    {
      title: "Request type",
      dataIndex: "request_type",
      key: "request_type",
      render: (text, record) => {
        return (
          <div>
            <div>{text || "-"}</div>
            {record.approval_role_name && (
              <div>
                <Text strong>{record.approval_role_name}</Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Requested By",
      dataIndex: "requested_by",
      key: "requested_by",
      render: (text) => text || "-",
    },
    {
      title: "Requested On",
      dataIndex: "requested_on",
      key: "requested_on",
      render: (text) => text || "-",
    },
    {
      title: "Remarks on Request",
      dataIndex: "remarks_on_request",
      key: "remarks_on_request",
      width: 200,
      render: (text) => renderRemarks(text, "Remarks on Request"),
    },
    {
      title: "Approval Status",
      dataIndex: "approval_status_name",
      key: "approval_status_name",
      render: (text) => text || "-",
    },
    {
      title: "Approved / Rejected On",
      dataIndex: "approved_rejected_on",
      key: "approved_rejected_on",
      render: (text) => text || "-",
    },
    {
      title: "Remarks on Approve / Reject",
      dataIndex: "approve_reject_remarks",
      key: "approve_reject_remarks",
      width: 200,
      render: (text) => renderRemarks(text, "Remarks on Approve / Reject"),
    },
    {
      title: "Approved / Rejected By",
      dataIndex: "approved_rejected_by",
      key: "approved_rejected_by",
      render: (text) => text || "-",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const showButtons = shouldShowActionButtons(record);
        if (!showButtons) {
          return "-";
        }
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              className="secondary-button"
              onClick={() => onApprove(record, "Delivery Request")}
            >
              Approve
            </Button>
            <Button
              ghost
              danger
              size="small"
              className="primary-button"
              onClick={() => onReject(record, "Delivery Request")}
            >
              Reject
            </Button>
          </Space>
        );
      },
    },
  ];

  // Cancellation Request columns
  const getCancellationColumns = (onApprove, onReject) => [
    {
      title: "Request ID",
      dataIndex: "request_id",
      key: "request_id",
    },
    {
      title: "Request type",
      dataIndex: "request_type",
      key: "request_type",
      render: (text, record) => {
        return (
          <div>
            <div>{text || "-"}</div>
            {record.approval_role_name && (
              <div>
                <Text strong>{record.approval_role_name}</Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Customer Cancellation Letter",
      dataIndex: "customer_cancellation_letter",
      key: "customer_cancellation_letter",
      render: (text, record) => {
        if (record.cancellation_letter_path) {
          return (
            <a
              href={record.cancellation_letter_path}
              target="_blank"
              rel="noopener noreferrer"
            >
              {text || record.cancellation_letter_file_name || "View Document"}
            </a>
          );
        }
        return text || "-";
      },
    },
    {
      title: "Requested By",
      dataIndex: "requested_by",
      key: "requested_by",
    },
    {
      title: "Requested On",
      dataIndex: "requested_on",
      key: "requested_on",
    },
    {
      title: "Remarks on Request",
      dataIndex: "remarks_on_request",
      key: "remarks_on_request",
      width: 200,
      render: (text) => renderRemarks(text, "Remarks on Request"),
    },
    {
      title: "Approval Status",
      dataIndex: "approval_status_name",
      key: "approval_status_name",
    },
    {
      title: "Approved / Rejected On",
      dataIndex: "approved_rejected_on",
      key: "approved_rejected_on",
      render: (text) => text || "-",
    },
    {
      title: "Remarks on Approve / Reject",
      dataIndex: "approve_reject_remarks",
      key: "approve_reject_remarks",
      width: 200,
      render: (text) => renderRemarks(text, "Remarks on Approve / Reject"),
    },
    {
      title: "Approved / Rejected By",
      dataIndex: "approved_rejected_by",
      key: "approved_rejected_by",
      render: (text) => text || "-",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        // Don't show buttons for Refund Request
        const isRefundRequest =
          record.request_type_id === REQUEST_TYPE_IDS.REFUND_REQUEST ||
          record.request_type?.toLowerCase().includes("refund");
        if (isRefundRequest) {
          return "-";
        }
        const showButtons = shouldShowActionButtons(record);
        if (!showButtons) {
          return "-";
        }
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              className="secondary-button"
              onClick={() => onApprove(record, "Cancellation Request")}
            >
              Approve
            </Button>
            <Button
              ghost
              danger
              size="small"
              className="primary-button"
              onClick={() => onReject(record, "Cancellation Request")}
            >
              Reject
            </Button>
          </Space>
        );
      },
    },
  ];

  // Filter request types based on category using constants
  const getFilteredRequestTypes = (category) => {
    if (category === "Allocation Request") {
      // Allocation Request: id 1, 2
      let filteredTypes = requestTypes.filter((type) =>
        REQUEST_TYPE_CATEGORIES.ALLOCATION.includes(type.id)
      );
      // Hide only ALLOCATION_REQUEST (id 1) if allocate_vin_button_enabled is false
      if (!leadViewDetails?.allocate_vin_button_enabled) {
        filteredTypes = filteredTypes.filter(
          (type) => type.id !== REQUEST_TYPE_IDS.ALLOCATION_REQUEST
        );
      }
      return filteredTypes;
    } else if (category === "Delivery Request") {
      // Delivery Request: id 3, 4, 7
      return requestTypes.filter((type) =>
        REQUEST_TYPE_CATEGORIES.DELIVERY.includes(type.id)
      );
    } else if (category === "Cancellation Request") {
      // Cancellation Request: id 5, 6
      return requestTypes.filter((type) =>
        REQUEST_TYPE_CATEGORIES.CANCELLATION.includes(type.id)
      );
    }
    return [];
  };

  const handleNewRequest = (requestType) => {
    setRequestTypeForModal(requestType);
    setIsNewRequestModalOpen(true);
    setSelectedCancellationRequestType(null);
    // Fetch cancellation reasons if it's a cancellation request
    if (requestType === "Cancellation Request") {
      fetchCancellationReasons();
    }
  };

  const handleNewRequestCancel = () => {
    setIsNewRequestModalOpen(false);
    form.resetFields();
    setRequestTypeForModal("");
    setCancellationAttachments([]);
    setSelectedCancellationRequestType(null);
  };

  const handleNewRequestSubmit = async (values) => {
    try {
      // For Allocation and Delivery requests
      if (
        requestTypeForModal === "Allocation Request" ||
        requestTypeForModal === "Delivery Request"
      ) {
        const payload = {
          lead_id: leadId,
          request_type_id: values.request_type,
          request_creation_remarks: values.remarks || "",
        };

        const res = await createLeadRequestService(payload);
        if (res?.data?.status === 200 || res?.data?.status === 201) {
          notification.success({
            message: res?.data?.message || "Request created successfully",
          });
          setIsNewRequestModalOpen(false);
          form.resetFields();
          setRequestTypeForModal("");
          // Refresh lead details
          if (leadId) {
            dispatch(fetchLeadDetailsServices({ id: leadId }));
          }
          // Refresh all request listings (allocation, delivery, and cancellation)
          fetchAllRequests();
        }
      } else if (requestTypeForModal === "Cancellation Request") {
        // Handle cancellation request with file upload
        // For Refund Request (ID: 6), reason and attachment are not required
        const isRefundRequest =
          values.request_type === REQUEST_TYPE_IDS.REFUND_REQUEST;

        if (!isRefundRequest) {
          if (
            !cancellationAttachments?.length ||
            !cancellationAttachments.some((f) => f.originFileObj)
          ) {
            notification.error({
              description: "Please upload at least one attachment",
            });
            return;
          }
        }

        const formData = new FormData();
        formData.append("lead_id", leadId);
        formData.append("request_type_id", values.request_type);

        // Only append reason and attachments if not Refund Request
        if (!isRefundRequest) {
          formData.append("cancellation_reason_id", values.reason);
          // Append multiple attachments with index notation
          cancellationAttachments.forEach((file, index) => {
            if (file.originFileObj) {
              formData.append(`attachments[${index}]`, file.originFileObj);
            }
          });
        }

        if (values.remarks) {
          formData.append("request_creation_remarks", values.remarks);
        }

        const res = await createLeadRequestService(formData);
        if (res?.data?.status === 200 || res?.data?.status === 201) {
          notification.success({
            message:
              res?.data?.message || "Cancellation request created successfully",
          });
          setIsNewRequestModalOpen(false);
          form.resetFields();
          setRequestTypeForModal("");
          setCancellationAttachments([]);
          setSelectedCancellationRequestType(null);
          // Refresh lead details
          if (leadId) {
            dispatch(fetchLeadDetailsServices({ id: leadId }));
          }
          // Refresh all request listings (allocation, delivery, and cancellation)
          fetchAllRequests();
        }
      }
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    }
  };

  const handleApprove = (record) => {
    setCurrentRecord(record);
    setApprovalAction("approve");
    setIsApproveRejectModalOpen(true);
    approveRejectForm.resetFields();
  };

  const handleReject = (record) => {
    setCurrentRecord(record);
    setApprovalAction("reject");
    setIsApproveRejectModalOpen(true);
    approveRejectForm.resetFields();
  };

  const handleApproveRejectCancel = () => {
    setIsApproveRejectModalOpen(false);
    setCurrentRecord(null);
    setApprovalAction(null);
    approveRejectForm.resetFields();
  };

  const handleApproveRejectSubmit = async (values) => {
    if (!currentRecord || !leadId) {
      notification.error({
        description: "Missing required information",
      });
      return;
    }

    try {
      setApproveRejectLoading(true);

      const payload = {
        lead_id: leadId,
        request_id: currentRecord.id,
        request_type_id: currentRecord.request_type_id,
        approval_status: approvalAction === "approve" ? 1 : 2,
        approve_reject_remarks:
          approvalAction === "reject"
            ? values?.remarks || ""
            : currentRecord.approve_reject_remarks || "",
      };

      const res = await approveRejectLeadRequestService(payload);

      if (res?.data?.status === 200 || res?.data?.status === 201) {
        notification.success({
          message:
            res?.data?.message ||
            `${approvalAction === "approve" ? "Approved" : "Rejected"
            } successfully`,
        });
        setIsApproveRejectModalOpen(false);
        setCurrentRecord(null);
        setApprovalAction(null);
        approveRejectForm.resetFields();

        // Refresh all request tables (Allocation, Delivery, and Cancellation)
        fetchRequestsByType(
          "Allocation Request",
          allocationPagination.current,
          allocationPagination.pageSize,
          allocationSearch,
          setAllocationData,
          setAllocationLoading,
          setAllocationPagination
        );

        fetchRequestsByType(
          "Delivery Request",
          deliveryPagination.current,
          deliveryPagination.pageSize,
          deliverySearch,
          setDeliveryData,
          setDeliveryLoading,
          setDeliveryPagination
        );

        fetchRequestsByType(
          "Cancellation Request",
          cancellationPagination.current,
          cancellationPagination.pageSize,
          cancellationSearch,
          setCancellationData,
          setCancellationLoading,
          setCancellationPagination
        );
      } else {
        notification.error({
          description:
            res?.data?.message ||
            `Failed to ${approvalAction === "approve" ? "approve" : "reject"
            } request`,
        });
      }
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setApproveRejectLoading(false);
    }
  };

  const handleDownloadGatePass = async () => {
    try {
      const res = await downloadGatePassService(leadId);
      const pdfUrl = res?.data?.data;

      if (pdfUrl) {
        // Open the PDF in a new tab
        window.open(pdfUrl, "_blank");
        notification.success({
          message: res?.data?.message || "Gate pass downloaded successfully",
        });
      } else {
        notification.error({
          description: "Gate pass URL not found",
        });
      }
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    }
  };

  const handleAllocateVinNumber = () => {
    setIsAllocateVinModalOpen(true);
  };

  const handleAllocateVinCancel = () => {
    setIsAllocateVinModalOpen(false);
  };

  const handleAllocateVin = () => {
    // Refresh requests data after allocation
    fetchAllRequests();
    // Refresh lead details to update current status and allocated VIN button enabled/disabled flag
    if (leadId) {
      dispatch(fetchLeadDetailsServices({ id: leadId }));
    }
  };

  const handleAllocationSearch = (value) => {
    setAllocationSearch(value);
    fetchRequestsByType(
      "Allocation Request",
      1,
      allocationPagination.pageSize,
      value?.trim(),
      setAllocationData,
      setAllocationLoading,
      setAllocationPagination
    );
  };

  const handleDeliverySearch = (value) => {
    setDeliverySearch(value);
    fetchRequestsByType(
      "Delivery Request",
      1,
      deliveryPagination.pageSize,
      value?.trim(),
      setDeliveryData,
      setDeliveryLoading,
      setDeliveryPagination
    );
  };

  const handleCancellationSearch = (value) => {
    setCancellationSearch(value);
    fetchRequestsByType(
      "Cancellation Request",
      1,
      cancellationPagination.pageSize,
      value?.trim(),
      setCancellationData,
      setCancellationLoading,
      setCancellationPagination
    );
  };

  // Combined loading state for the main loader
  const isLoading = allocationLoading || deliveryLoading || cancellationLoading;

  return (
    <>
      <Loader loading={isLoading} />
      <div className="user-data">
        <div className="personal-detail">
          {/* Common Download Gate Pass Button */}
          <div className="mb-3">
            <Row justify="space-between" align="middle">
              <Col flex="auto" style={{ textAlign: "center" }}>
                {leadViewDetails?.current_status && (
                  <Typography.Text
                    strong
                    style={{ fontSize: "16px", color: "red" }}
                  >
                    Current Status: {leadViewDetails.current_status}
                  </Typography.Text>
                )}
              </Col>
              <Col>
                {!isViewOnlyRole && (
                  <Button
                    type="primary"
                    className="secondary-button"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadGatePass}
                    disabled={!leadViewDetails?.gatepass_enabled}
                  >
                    Download Gate Pass
                  </Button>
                )}
              </Col>
            </Row>
          </div>

          {/* Allocation Request Section */}
          <Flex vertical gap={16}>
            <Card>
              <Title level={5} style={{ marginBottom: "15px" }}>
                Allocation Request
              </Title>
              <Flex vertical gap={16}>
                <Flex
                  gap={12}
                  align="center"
                  justify="space-between"
                  className="flex-wrap"
                >
                  <Input.Search
                    placeholder="Search by remarks"
                    allowClear
                    value={allocationSearch}
                    onChange={(e) => setAllocationSearch(e.target.value)}
                    onSearch={handleAllocationSearch}
                    onPressEnter={(e) => handleAllocationSearch(e.target.value)}
                    style={{ width: 260 }}
                    className="form-control-ant"
                  />
                  {canCreateRequest && isLeadOpen && (
                    <Flex align="center" gap={12} className="flex-wrap">
                      <Button
                        type="primary"
                        className="secondary-button"
                        onClick={() => handleNewRequest("Allocation Request")}
                        disabled={!isLeadOpen || leadViewDetails?.allocate_vin_button_enabled}
                      >
                        New Request
                      </Button>
                      <Button
                        type="primary"
                        className="secondary-button"
                        onClick={handleAllocateVinNumber}
                        disabled={!leadViewDetails?.allocate_vin_button_enabled}
                      >
                        Allocate Vin Number
                      </Button>
                    </Flex>
                  )}
                </Flex>
                <Table
                  columns={getAllocationColumns(handleApprove, handleReject)}
                  dataSource={allocationData}
                  loading={allocationLoading}
                  pagination={{
                    current: allocationPagination.current,
                    pageSize: allocationPagination.pageSize,
                    total: allocationPagination.total,
                    showSizeChanger: true,
                  }}
                  onChange={(pagination) => {
                    const { current, pageSize } = pagination;
                    fetchRequestsByType(
                      "Allocation Request",
                      current,
                      pageSize,
                      allocationSearch,
                      setAllocationData,
                      setAllocationLoading,
                      setAllocationPagination
                    );
                  }}
                  locale={{
                    emptyText: "No requests found",
                  }}
                  scroll={{ x: "max-content" }}
                />
              </Flex>
            </Card>
            <Card>
              <Title level={5} style={{ marginBottom: "15px" }}>
                Delivery Request
              </Title>
              <Flex vertical gap={16}>
                <Flex
                  gap={12}
                  align="center"
                  justify="space-between"
                  className="flex-wrap"
                >
                  <Input.Search
                    placeholder="Search by remarks"
                    allowClear
                    value={deliverySearch}
                    onChange={(e) => setDeliverySearch(e.target.value)}
                    onSearch={handleDeliverySearch}
                    onPressEnter={(e) => handleDeliverySearch(e.target.value)}
                    style={{ width: 260 }}
                    className="form-control-ant"
                  />
                  {canCreateRequest && isLeadOpen && (
                    <>
                      <Button
                        type="primary"
                        className="secondary-button"
                        onClick={() => handleNewRequest("Delivery Request")}
                        disabled={!isLeadOpen}
                      >
                        New Request
                      </Button>
                    </>
                  )}
                </Flex>

                <Table
                  columns={getDeliveryColumns(handleApprove, handleReject)}
                  dataSource={deliveryData}
                  loading={deliveryLoading}
                  pagination={{
                    current: deliveryPagination.current,
                    pageSize: deliveryPagination.pageSize,
                    total: deliveryPagination.total,
                    showSizeChanger: true,
                  }}
                  onChange={(pagination) => {
                    const { current, pageSize } = pagination;
                    fetchRequestsByType(
                      "Delivery Request",
                      current,
                      pageSize,
                      deliverySearch,
                      setDeliveryData,
                      setDeliveryLoading,
                      setDeliveryPagination
                    );
                  }}
                  locale={{
                    emptyText: "No requests found",
                  }}
                  scroll={{ x: "max-content" }}
                />
              </Flex>
            </Card>

            <Card>
              <Title level={5} style={{ marginBottom: "15px" }}>
                Cancellation Request
              </Title>
              <Flex vertical gap={16}>
                <Flex
                  gap={12}
                  align="center"
                  justify="space-between"
                  className="flex-wrap"
                >
                  <Input.Search
                    placeholder="Search by remarks"
                    allowClear
                    value={cancellationSearch}
                    onChange={(e) => setCancellationSearch(e.target.value)}
                    onSearch={handleCancellationSearch}
                    onPressEnter={(e) => handleCancellationSearch(e.target.value)}
                    style={{ width: 260 }}
                    className="form-control-ant"
                  />
                  {canCreateRequest && isLeadOpen && (
                    <>
                      <Button
                        type="primary"
                        className="secondary-button"
                        onClick={() => handleNewRequest("Cancellation Request")}
                        disabled={!isLeadOpen}
                      >
                        New Request
                      </Button>
                    </>
                  )}
                </Flex>

                <Table
                  columns={getCancellationColumns(handleApprove, handleReject)}
                  dataSource={cancellationData}
                  loading={cancellationLoading}
                  pagination={{
                    current: cancellationPagination.current,
                    pageSize: cancellationPagination.pageSize,
                    total: cancellationPagination.total,
                    showSizeChanger: true,
                  }}
                  onChange={(pagination) => {
                    const { current, pageSize } = pagination;
                    fetchRequestsByType(
                      "Cancellation Request",
                      current,
                      pageSize,
                      cancellationSearch,
                      setCancellationData,
                      setCancellationLoading,
                      setCancellationPagination
                    );
                  }}
                  locale={{
                    emptyText: "No requests found",
                  }}
                  scroll={{ x: "max-content" }}
                />
              </Flex>
            </Card>
          </Flex>
        </div>

        {/* New Request Modal */}
        <Modal
          title={
            <div>
              <h5
                className="text-center mb-0"
                style={{ fontSize: "20px", fontWeight: "500" }}
              >
                {requestTypeForModal || "New Request"}
              </h5>
              <hr style={{ margin: "15px 0 0 0" }} />
            </div>
          }
          open={isNewRequestModalOpen}
          onCancel={handleNewRequestCancel}
          footer={[
            <Button
              key="cancel"
              onClick={handleNewRequestCancel}
              ghost
              danger
              className="primary-button"
            >
              Cancel
            </Button>,
            <Button
              key="create"
              type="primary"
              className="secondary-button"
              onClick={() => form.submit()}
            >
              Create
            </Button>,
          ]}
          maskClosable={false}
          width={800}
        >
          <Form form={form} layout="vertical" onFinish={handleNewRequestSubmit}>
            <Row gutter={[16, 16]}>
              {/* For Cancellation Request: Show Request Type, Reason, Attachment and Remarks */}
              {requestTypeForModal === "Cancellation Request" ? (
                <>
                  <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                    <Form.Item
                      name="request_type"
                      label="Request Type"
                      rules={[
                        { required: true, message: "Please select request type" },
                      ]}
                    >
                      <Select
                        placeholder="Select Request Type"
                        className="form-control-ant"
                        onChange={(value) => {
                          setSelectedCancellationRequestType(value);
                          // Reset reason and attachments when switching request type
                          if (value === REQUEST_TYPE_IDS.REFUND_REQUEST) {
                            form.setFieldsValue({ reason: undefined });
                            setCancellationAttachments([]);
                          }
                        }}
                      >
                        {getFilteredRequestTypes("Cancellation Request")?.map(
                          (option) => (
                            <Select.Option key={option.id} value={option.id}>
                              {option?.request_type}
                            </Select.Option>
                          )
                        )}
                      </Select>
                    </Form.Item>
                  </Col>

                  {selectedCancellationRequestType !==
                    REQUEST_TYPE_IDS.REFUND_REQUEST && (
                      <>
                        <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                          <Form.Item
                            name="reason"
                            label="Reason"
                            rules={[
                              { required: true, message: "Please select reason" },
                            ]}
                          >
                            <Select
                              placeholder="Select Reason"
                              className="form-control-ant"
                            >
                              {cancellationReasons?.map((option) => (
                                <Select.Option key={option.id} value={option.id}>
                                  {option?.reason}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                          <Form.Item
                            name="attachments"
                            label="Attachment"
                            required
                            rules={[
                              {
                                validator: () => {
                                  if (
                                    !cancellationAttachments?.length ||
                                    !cancellationAttachments.some(
                                      (f) => f.originFileObj
                                    )
                                  ) {
                                    return Promise.reject(
                                      "Please upload attachment"
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <Upload
                              multiple
                              maxCount={5}
                              beforeUpload={validateCancellationFile}
                              accept=".jpg,.jpeg,.png,.gif,.pdf"
                              customRequest={({ file, onSuccess }) => {
                                console.log(file);
                                onSuccess("ok");
                              }}
                              onChange={handleCancellationFileChange}
                              fileList={cancellationAttachments}
                              showUploadList={{ showRemoveIcon: true }}
                            >
                              <Button icon={<UploadOutlined />}></Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                      </>
                    )}

                  <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                    <Form.Item
                      name="remarks"
                      label="Remarks"
                      rules={[{ required: false }]}
                    >
                      <TextArea
                        rows={4}
                        placeholder="Type the remarks"
                        className="form-control-ant"
                      />
                    </Form.Item>
                  </Col>
                </>
              ) : (
                /* For Allocation and Delivery Request: Show Request Type and Remarks */
                <>
                  <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                    <Form.Item
                      name="request_type"
                      label="Request Type"
                      rules={[
                        { required: true, message: "Please select request type" },
                      ]}
                    >
                      <Select
                        placeholder="Select One"
                        className="form-control-ant"
                      >
                        {getFilteredRequestTypes(requestTypeForModal)?.map(
                          (option) => (
                            <Select.Option key={option.id} value={option.id}>
                              {option?.request_type}
                            </Select.Option>
                          )
                        )}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                    <Form.Item
                      name="remarks"
                      label="Remarks"
                      rules={[{ required: false }]}
                    >
                      <TextArea
                        rows={4}
                        placeholder="Type the remarks if required"
                        className="form-control-ant"
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        </Modal>

        {/* Allocate VIN Number Modal */}
        <AllocateVinModal
          open={isAllocateVinModalOpen}
          onCancel={handleAllocateVinCancel}
          onAllocate={handleAllocateVin}
          leadId={leadId}
          expectedDeliveryDate={leadViewDetails?.expected_delivery_date}
          customerName={leadViewDetails?.customer?.full_name || "N/A"}
        />

        {/* Approve/Reject Modal */}
        <Modal
          title={
            <div>
              <h5
                className="text-center mb-0"
                style={{ fontSize: "20px", fontWeight: "500" }}
              >
                Confirmation
              </h5>
              <hr style={{ margin: "15px 0 0 0" }} />
            </div>
          }
          open={isApproveRejectModalOpen}
          onCancel={handleApproveRejectCancel}
          footer={[
            <Button
              key="cancel"
              onClick={handleApproveRejectCancel}
              ghost
              danger
              className="primary-button"
              disabled={approveRejectLoading}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              className="secondary-button"
              onClick={() => approveRejectForm.submit()}
              loading={approveRejectLoading}
              style={
                approvalAction === "reject"
                  ? { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f" }
                  : {}
              }
            >
              {approvalAction === "approve" ? "Approve" : "Reject"}
            </Button>,
          ]}
          maskClosable={false}
          width={600}
        >
          <Form
            form={approveRejectForm}
            layout="vertical"
            onFinish={handleApproveRejectSubmit}
          >
            <div style={{ padding: "20px 0" }}>
              {currentRecord && (
                <p
                  style={{
                    fontSize: "16px",
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  You are about to{" "}
                  <strong>
                    {approvalAction === "approve" ? "Approve" : "Reject"}
                  </strong>{" "}
                  the <strong>{currentRecord.request_type || "Request"}</strong>
                  {(currentRecord.request_type_id === REQUEST_TYPE_IDS.ALLOCATION_REQUEST ||
                    currentRecord.request_type_id === REQUEST_TYPE_IDS.ALLOCATION_CANCELLATION_REQUEST) && (
                    <> for Chassis Number{" "}
                      <strong>{currentRecord.chassis_number || "N/A"}</strong></>
                  )}
                  {" "}for the Customer{" "}
                  <strong>{leadViewDetails?.customer?.full_name || "N/A"}</strong>
                  . Please click{" "}
                  <strong>
                    {approvalAction === "approve" ? "Approve" : "Reject"}
                  </strong>{" "}
                  to proceed or close the box.
                </p>
              )}
              {approvalAction === "reject" && (
                <Form.Item
                  name="remarks"
                  label="Remarks"
                  rules={[
                    { required: true, message: "Please enter remarks" },
                    {
                      max: 200,
                      message: "Remarks cannot exceed 200 characters",
                    },
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Enter remarks"
                    maxLength={200}
                    showCount
                  />
                </Form.Item>
              )}
            </div>
          </Form>
        </Modal>

        {/* Remarks Modal */}
        <Modal
          title={remarksModalTitle}
          open={remarksModalVisible}
          onCancel={() => {
            setRemarksModalVisible(false);
            setSelectedRemarks("");
            setRemarksModalTitle("Remarks");
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setRemarksModalVisible(false);
                setSelectedRemarks("");
                setRemarksModalTitle("Remarks");
              }}
            >
              Close
            </Button>,
          ]}
          width={600}
        >
          <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {selectedRemarks}
          </p>
        </Modal>
      </div>
    </>
  );
};

export default Requests;
